/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import FormHelperText from '@mui/material/FormHelperText'
import Table from 'components/Table'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { EditItemModal } from './EditItemModal'
import * as classes from './styles'
import {
  EditingItem,
  getTableOfContent,
  updateItemInHtml,
  validateSlug,
  validateTitle,
  validateTableOfContent,
  ValidateTableOfContentOptions,
} from './helpers'

export type TableOfContentProps = {
  value: string // html string
  isTouched?: boolean
  onChange: (value: string) => void
  className?: string
  validateOptions?: ValidateTableOfContentOptions
}

export const TableOfContent = ({ value, isTouched, onChange, className, validateOptions }: TableOfContentProps) => {
  const items = useMemo(() => getTableOfContent(value), [value])
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null)

  const { error, errors } = useMemo(() => validateTableOfContent(items, validateOptions), [items])
  const handleValidateSlug = useCallback((slug: string, isNew?: boolean) => validateSlug(items, slug, isNew), [items])
  const handleValidateTitle = useCallback(
    (title: string, isNew?: boolean) => validateTitle(items, title, isNew),
    [items]
  )

  const handleSubmit = (updatedItem: { slug: string; title: string }) => {
    if (!editingItem || !updatedItem) {
      return
    }

    const updatedValue = updateItemInHtml(
      {
        ...editingItem,
        newSlug: updatedItem.slug,
        newTitle: updatedItem.title,
      },
      value
    )
    onChange(updatedValue)
    setEditingItem(null)
  }

  const formattedItems = items.map((item, index) => ({ ...item, id: String(index) }))

  return (
    <div className={className}>
      <div css={classes.title}>Table of Contents</div>
      {formattedItems.length ? (
        <Table
          css={classes.table}
          columns={[
            {
              title: 'Index',
              renderCell: (_, index) => `${index + 1}.`,
              flex: '0 1 30px',
            },
            {
              title: 'Title',
              renderCell: ({ title }, index) => <span css={errors[index].title && classes.errorCell}>{title}</span>,
            },
            {
              title: 'Slug',
              renderCell: ({ slug }, index) => <span css={errors[index].slug && classes.errorCell}>#{slug}</span>,
            },
            {
              renderCell: ({ slug, title }, index) => (
                <IconButton
                  size="small"
                  onClick={() =>
                    setEditingItem({
                      slug,
                      title,
                      nth: items.slice(0, index).filter((item) => item.slug === slug).length + 1,
                      index,
                    })
                  }
                >
                  <EditIcon />
                </IconButton>
              ),
              flex: '0 1 30px',
            },
          ]}
          items={formattedItems}
          hideHeader
        />
      ) : (
        <div css={classes.emptyBlock}>No items at the moment</div>
      )}
      {!!error && isTouched && <FormHelperText error>{error}</FormHelperText>}
      <EditItemModal
        item={editingItem}
        onClose={() => setEditingItem(null)}
        onSubmit={handleSubmit}
        validateSlug={handleValidateSlug}
        validateTitle={handleValidateTitle}
      />
    </div>
  )
}
